<template>
  <v-card height="100%" flat color="bg1" class="release-detail overflow-y-auto">
    <!-- Feature flag off: the whole workspace collapses to the shared notice (§7 / addendum F) -->
    <ReleaseNotesDisabledNotice v-if="featureDisabled" />

    <div v-else-if="notFound" class="ds-empty-state">
      <v-icon class="ds-empty-state__icon">mdi-file-remove-outline</v-icon>
      <div class="ds-empty-state__title">Release not found</div>
      <div class="ds-empty-state__description">
        This release no longer exists — it may have been removed since the link was created.
      </div>
      <v-btn class="ds-empty-state__action" small outlined @click="goToList">
        <v-icon left small>mdi-arrow-left</v-icon>
        Back to Release Notes
      </v-btn>
    </div>

    <template v-else>
      <!-- Page header -->
      <v-list-item two-line :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
        <v-list-item-content>
          <v-list-item-title class="title d-flex align-center">
            <template v-if="editingName">
              <v-text-field
                v-model="nameDraft"
                outlined
                dense
                hide-details="auto"
                autofocus
                maxlength="200"
                :rules="[formRules.required]"
                :disabled="savingName"
                class="release-detail__name-field"
                @keyup.enter="saveName"
                @keyup.esc="cancelRename" />
              <v-btn
                icon
                small
                class="ml-2"
                :loading="savingName"
                :disabled="!isNameValid"
                @click="saveName">
                <v-icon small>mdi-check</v-icon>
              </v-btn>
              <v-btn icon small :disabled="savingName" @click="cancelRename">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
            <template v-else>
              <span class="text-truncate">{{ releaseName }}</span>
              <ReleaseStatusChip
                v-if="releaseStatus !== null"
                :status="releaseStatus"
                class="ml-3" />
              <v-tooltip v-if="canEdit && nameLocked" bottom max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small class="ml-2" v-bind="attrs" v-on="on">mdi-lock-outline</v-icon>
                </template>
                <span>{{ nameLockedHint }}</span>
              </v-tooltip>
              <v-btn
                v-else-if="canEdit"
                icon
                x-small
                class="ml-2"
                :disabled="!release"
                @click="startRename">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>{{ subtitle }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <div class="d-flex align-center">
            <v-btn small text class="mr-2" @click="goToList">
              <v-icon left small>mdi-arrow-left</v-icon>
              Back to list
            </v-btn>
            <v-btn
              v-if="showArchive"
              small
              text
              color="error"
              class="mr-2"
              :disabled="isBusy"
              @click="confirmArchive">
              <v-icon left small>mdi-archive-outline</v-icon>
              Archive
            </v-btn>
            <v-btn
              v-if="showPublish"
              small
              color="primary"
              :disabled="isBusy"
              @click="publishDialog = true">
              <v-icon left small>mdi-send-outline</v-icon>
              Publish
            </v-btn>
            <v-btn
              v-else-if="showRenotify"
              small
              outlined
              color="primary"
              :disabled="isBusy"
              @click="publishDialog = true">
              <v-icon left small>mdi-email-sync-outline</v-icon>
              Re-notify
            </v-btn>
          </div>
        </v-list-item-action>
      </v-list-item>
      <v-divider></v-divider>

      <div v-if="showBanner || pollTimedOut" class="px-5 pt-4">
        <GenerationStatusBanner
          v-if="showBanner"
          :release="release"
          @regenerate="openRegenerate"
          @refresh="manualRefresh" />
        <div v-if="pollTimedOut" class="ds-info-box ds-info-box--warning mt-3">
          <v-icon class="ds-info-box__icon">mdi-clock-alert-outline</v-icon>
          <div class="ds-info-box__content">
            This release is still generating after several minutes. Stopped checking automatically —
            use Refresh to check again.
          </div>
        </div>
      </div>

      <div class="d-flex align-center pr-4">
        <v-tabs v-model="tab" dense height="36" class="flex-grow-1">
          <v-tab>Notes</v-tab>
          <v-tab>Items</v-tab>
          <v-tab>Publish</v-tab>
        </v-tabs>
        <v-btn v-if="tab === 0" small text @click="openHistory">
          <v-icon left small>mdi-history</v-icon>
          History
        </v-btn>
      </div>
      <v-divider />

      <v-card-text class="pa-0">
        <v-tabs-items v-model="tab">
          <!-- Notes: two independent streams — what customers get, and what we keep for ourselves -->
          <v-tab-item>
            <div class="d-flex align-center px-4 pt-3">
              <v-btn-toggle v-model="notesKind" dense mandatory color="primary">
                <v-btn small :value="draftKinds.Customer">
                  <v-icon left small>mdi-account-group-outline</v-icon>
                  Customer
                </v-btn>
                <v-btn small :value="draftKinds.Internal">
                  <v-icon left small>mdi-shield-lock-outline</v-icon>
                  Internal
                </v-btn>
              </v-btn-toggle>
              <span v-if="isInternalNotes" class="caption ml-3 text--secondary">
                <v-icon x-small class="mr-1">mdi-shield-lock-outline</v-icon>
                Internal only — never emailed to customers or shown on their release page.
              </span>
            </div>

            <DraftEditor
              :key="notesKind"
              :release="release"
              :release-id="id"
              :kind="notesKind"
              @saved="reload"
              @reload="reload"
              @regenerated="reload" />
          </v-tab-item>

          <!-- Items -->
          <v-tab-item>
            <ItemsPanel
              :release="release"
              :release-id="id"
              @refresh="reload"
              @regenerate="openRegenerate" />
          </v-tab-item>

          <!-- Publish -->
          <v-tab-item>
            <div class="pa-5">
              <div v-if="showPublishActions" class="d-flex align-center justify-end mb-4">
                <v-btn
                  v-if="canEdit"
                  small
                  outlined
                  class="mr-2"
                  :loading="sendingTest"
                  @click="sendTest">
                  <v-icon left small>mdi-email-outline</v-icon>
                  Send test email
                </v-btn>
                <v-btn
                  v-if="showMarkAppReleased"
                  small
                  color="primary"
                  :disabled="isBusy"
                  @click="confirmMarkAppReleased">
                  <v-icon left small>mdi-rocket-launch-outline</v-icon>
                  Mark app released
                </v-btn>
              </div>
              <PublicationHistory :release-id="id" />
              <ReminderPanel
                class="mt-6"
                :release="release"
                :release-id="id"
                @changed="onReminderChanged" />
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <!-- Draft history drawer -->
      <v-navigation-drawer v-model="historyDrawer" fixed temporary right width="400">
        <div class="pa-4">
          <div class="ds-section-header">
            <div class="ds-section-header__content">
              <h3 class="ds-section-header__title">Draft history</h3>
              <p class="ds-section-header__subtitle">
                Every saved, generated and reverted version.
              </p>
            </div>
            <div class="ds-section-header__actions">
              <v-btn icon small @click="historyDrawer = false">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
          <DraftHistoryList
            :drafts="drafts"
            :loading="draftsLoading"
            :current-draft-id="currentDraftId"
            @preview="previewDraft"
            @revert="confirmRevert" />
        </div>
      </v-navigation-drawer>

      <!-- Read-only preview of a historical draft -->
      <v-dialog v-model="previewDialog" max-width="820" scrollable>
        <v-card class="ds-dialog">
          <div class="ds-dialog__header">
            <v-icon class="ds-dialog__header-icon">mdi-file-document-outline</v-icon>
            <div class="ds-dialog__header-text">
              <div class="ds-dialog__title">{{ previewTitle }}</div>
              <div class="ds-dialog__subtitle">Read-only preview of a previous version.</div>
            </div>
            <v-spacer />
            <v-btn icon small @click="previewDialog = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-card-text class="ds-dialog__body">
            <div v-if="previewLoading" class="text-center py-8">
              <v-progress-circular indeterminate size="28" width="2" color="primary" />
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-else class="release-body" v-html="previewHtml"></div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Regenerate with optional instructions -->
      <v-dialog v-model="regenerateDialog" max-width="560" persistent>
        <v-card class="ds-dialog">
          <div class="ds-dialog__header">
            <v-icon class="ds-dialog__header-icon">mdi-autorenew</v-icon>
            <div class="ds-dialog__header-text">
              <div class="ds-dialog__title">Regenerate release notes</div>
              <div class="ds-dialog__subtitle">
                The AI rewrites the notes from the current ticket selection. The existing draft is
                kept in history.
              </div>
            </div>
          </div>
          <v-card-text class="ds-dialog__body">
            <div class="ds-field-label">Instructions (optional)</div>
            <v-textarea
              v-model="regenerateInstructions"
              outlined
              dense
              rows="4"
              hide-details="auto"
              placeholder="e.g. Keep it short, group by area, no ticket keys."
              :disabled="regenerating" />
          </v-card-text>
          <v-card-actions class="ds-dialog__footer">
            <v-spacer />
            <v-btn text small :disabled="regenerating" @click="regenerateDialog = false">
              Cancel
            </v-btn>
            <v-btn small color="primary" :loading="regenerating" @click="submitRegenerate">
              Regenerate
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <PublishDialog
        v-model="publishDialog"
        :release="release"
        :release-id="id"
        @published="onPublished" />
    </template>
  </v-card>
</template>

<script>
  import { marked } from 'marked';
  import { $formatDate, $has } from '@Utils';
  import { log, warn } from '@/utils/debugLogger';
  import permissions from '@Share/config/permissions';
  import formRules from '@Share/config/form-rules';
  import {
    ReleaseStatus,
    ReleaseDraftKind,
    isFeatureDisabledError,
    releaseErrorCode,
  } from '@ReleaseNotes/config/enums';
  import ReleaseStatusChip from '@ReleaseNotes/components/ReleaseStatusChip.vue';
  import ReleaseNotesDisabledNotice from '@ReleaseNotes/components/ReleaseNotesDisabledNotice.vue';
  import GenerationStatusBanner from '@ReleaseNotes/components/GenerationStatusBanner.vue';
  import DraftEditor from '@ReleaseNotes/components/DraftEditor.vue';
  import DraftHistoryList from '@ReleaseNotes/components/DraftHistoryList.vue';
  import ItemsPanel from '@ReleaseNotes/components/ItemsPanel.vue';
  import PublishDialog from '@ReleaseNotes/components/PublishDialog.vue';
  import PublicationHistory from '@ReleaseNotes/components/PublicationHistory.vue';
  import ReminderPanel from '@ReleaseNotes/components/ReminderPanel.vue';

  /** How often the detail is re-fetched while the AI is still generating. */
  const POLL_INTERVAL_MS = 3000;
  /** Hard stop so a stuck server-side job never polls forever. */
  const POLL_MAX_MS = 5 * 60 * 1000;

  const NOTES_TAB = 0;
  const PUBLISH_TAB = 2;

  const NAME_LOCKED_HINT = 'The release name can’t change after it has been published.';

  export default {
    name: 'ReleaseDetail',
    components: {
      ReleaseStatusChip,
      ReleaseNotesDisabledNotice,
      GenerationStatusBanner,
      DraftEditor,
      DraftHistoryList,
      ItemsPanel,
      PublishDialog,
      PublicationHistory,
      ReminderPanel,
    },
    props: {
      id: {
        type: [String, Number],
        required: true,
      },
    },
    data() {
      return {
        permissions,
        formRules,
        draftKinds: ReleaseDraftKind,
        notesKind: ReleaseDraftKind.Customer,
        tab: NOTES_TAB,
        featureDisabled: false,
        notFound: false,
        editingName: false,
        nameDraft: '',
        savingName: false,
        sendingTest: false,
        publishDialog: false,
        historyDrawer: false,
        previewDialog: false,
        previewLoading: false,
        previewDraftItem: null,
        regenerateDialog: false,
        regenerateInstructions: '',
        pollTimer: null,
        pollInFlight: false,
        pollStartedAt: 0,
        pollTimedOut: false,
      };
    },
    computed: {
      isInternalNotes() {
        return this.notesKind === ReleaseDraftKind.Internal;
      },
      /**
       * The release currently in the store, but only when it belongs to this route's id — the
       * detail slice is shared state, so a stale release from a previously opened one must not
       * render while this one is still loading.
       * @returns {object|null}
       */
      release() {
        const current = this.$store.getters['RELEASE_NOTES/GET_CURRENT_RELEASE'];
        const currentId = this.$store.getters['RELEASE_NOTES/GET_CURRENT_RELEASE_ID'];
        if (!current || String(currentId) !== String(this.id)) return null;
        return current;
      },
      isLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_CURRENT_RELEASE_LOADING'];
      },
      drafts() {
        return this.$store.getters['RELEASE_NOTES/GET_DRAFTS'];
      },
      draftsLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_DRAFTS_LOADING'];
      },
      regenerating() {
        return this.$store.getters['RELEASE_NOTES/IS_REGENERATING'];
      },
      reminders() {
        return this.$store.getters['RELEASE_NOTES/GET_REMINDERS'];
      },
      /** Blocks the destructive header actions while any detail request is in flight. */
      isBusy() {
        return this.isLoading || this.savingName;
      },
      releaseName() {
        if (!this.release) return 'Release';
        return this.release.name || this.release.Name || 'Untitled release';
      },
      releaseStatus() {
        if (!this.release) return null;
        const value = this.release.status !== undefined ? this.release.status : this.release.Status;
        return value === undefined || value === null ? null : value;
      },
      currentDraftId() {
        if (!this.release) return null;
        const value =
          this.release.currentDraftId !== undefined
            ? this.release.currentDraftId
            : this.release.CurrentDraftId;
        return value === undefined ? null : value;
      },
      firstPublishedAt() {
        if (!this.release) return null;
        return (
          this.release.firstPublishedAt ||
          this.release.firstPublishedAtUtc ||
          this.release.FirstPublishedAt ||
          null
        );
      },
      /**
       * The API rejects PATCH {id} once a release has been published, so the edit affordance is
       * replaced by a lock icon + tooltip rather than failing on submit.
       */
      nameLocked() {
        return !!this.firstPublishedAt || this.releaseStatus === ReleaseStatus.Published;
      },
      nameLockedHint() {
        return NAME_LOCKED_HINT;
      },
      isNameValid() {
        const value = (this.nameDraft || '').trim();
        return value.length > 0 && value !== this.releaseName;
      },
      subtitle() {
        if (!this.release) return this.isLoading ? 'Loading…' : '';
        const parts = [];
        const version =
          this.release.jiraVersionName || this.release.jiraVersion || this.release.jiraVersionId;
        if (version) parts.push(`Jira version ${version}`);
        const created = this.release.createdAt || this.release.createdOn;
        if (created) parts.push(`Created ${$formatDate(created, 'DD/MM/YYYY HH:mm')}`);
        if (this.firstPublishedAt) {
          parts.push(`Published ${$formatDate(this.firstPublishedAt, 'DD/MM/YYYY HH:mm')}`);
        }
        return parts.join(' · ');
      },
      canEdit() {
        return $has(permissions.ReleaseNotes.Edit);
      },
      canPublish() {
        return $has(permissions.ReleaseNotes.Publish);
      },
      showArchive() {
        return (
          this.canEdit &&
          (this.releaseStatus === ReleaseStatus.Draft ||
            this.releaseStatus === ReleaseStatus.GenerationFailed)
        );
      },
      showPublish() {
        return this.canPublish && this.releaseStatus === ReleaseStatus.Draft;
      },
      showRenotify() {
        return this.canPublish && this.releaseStatus === ReleaseStatus.Published;
      },
      showPublishActions() {
        return this.canEdit || this.showMarkAppReleased;
      },
      /**
       * "Mark app released" completes a pending reminder, so it only makes sense once a reminder
       * exists (armed at publish time) or has already fired.
       */
      showMarkAppReleased() {
        if (!this.canPublish || !this.release) return false;
        const pending = this.release.pendingReminder || this.release.PendingReminder;
        return !!pending || (Array.isArray(this.reminders) && this.reminders.length > 0);
      },
      showBanner() {
        return (
          this.releaseStatus === ReleaseStatus.Generating ||
          this.releaseStatus === ReleaseStatus.GenerationFailed
        );
      },
      previewTitle() {
        const draft = this.previewDraftItem;
        if (!draft) return 'Draft preview';
        const version = draft.version !== undefined ? draft.version : draft.Version;
        return version ? `Draft version ${version}` : 'Draft preview';
      },
      /**
       * The server's sanitized HTML is preferred; a draft that only carries Markdown is rendered
       * client-side with `marked`, matching the editor's preview pane.
       * @returns {string}
       */
      previewHtml() {
        const draft = this.previewDraftItem;
        if (!draft) return '';
        const html = draft.contentHtml || draft.html || draft.ContentHtml;
        if (typeof html === 'string' && html.trim().length > 0) return html;
        const markdown = draft.markdown || draft.content || draft.Markdown;
        if (typeof markdown === 'string' && markdown.trim().length > 0) {
          return marked.parse(markdown);
        }
        return '<p>This version has no content.</p>';
      },
    },
    watch: {
      id() {
        this.resetForRouteChange();
        this.reload();
      },
      releaseStatus() {
        this.syncPolling();
      },
      tab(value) {
        if (value === PUBLISH_TAB) this.fetchReminders();
      },
      historyDrawer(open) {
        if (open) this.fetchDrafts();
      },
    },
    created() {
      this.reload();
    },
    beforeDestroy() {
      this.stopPolling();
    },
    methods: {
      $has,

      /**
       * The single re-sync every child event triggers (plan §8). Also latches the two terminal
       * empty states on failure — `FETCH_RELEASE_DETAIL` re-throws, so it must be caught here.
       * @returns {Promise<void>}
       */
      reload() {
        this.featureDisabled = false;
        this.notFound = false;
        return this.$store
          .dispatch('RELEASE_NOTES/FETCH_RELEASE_DETAIL', this.id)
          .then(() => {
            this.syncPolling();
          })
          .catch((err) => {
            // Both cases are 404s — the release-specific code wins over the feature flag.
            if (releaseErrorCode(err) === 'RELEASE_NOT_FOUND') this.notFound = true;
            else if (isFeatureDisabledError(err)) this.featureDisabled = true;
            this.stopPolling();
            warn('ReleaseDetail: failed to load release', err);
          });
      },

      /** Clears every per-release piece of local state when the route id changes. */
      resetForRouteChange() {
        this.stopPolling();
        this.pollTimedOut = false;
        this.tab = NOTES_TAB;
        this.editingName = false;
        this.nameDraft = '';
        this.historyDrawer = false;
        this.previewDialog = false;
        this.previewDraftItem = null;
        this.publishDialog = false;
        this.regenerateDialog = false;
        this.regenerateInstructions = '';
      },

      // ------------------------------------------------------------- polling
      /**
       * Starts or stops the generation poll to match the current status. Called after every
       * successful fetch and whenever the status changes, so the interval dies the moment the
       * release leaves `Generating`.
       */
      syncPolling() {
        if (this.releaseStatus !== ReleaseStatus.Generating) {
          // Generation finished (or the release never was generating): drop the interval and the
          // stale "still generating" note in one go.
          this.pollTimedOut = false;
          this.stopPolling();
          return;
        }
        if (this.pollTimedOut) {
          this.stopPolling();
          return;
        }
        this.startPolling();
      },
      startPolling() {
        if (this.pollTimer) return;
        this.pollStartedAt = Date.now();
        this.pollTimer = setInterval(this.pollTick, POLL_INTERVAL_MS);
        log('ReleaseDetail: polling generation status');
      },
      stopPolling() {
        if (!this.pollTimer) return;
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      },
      pollTick() {
        // Never let two polls overlap — a slow response would otherwise stack requests.
        if (this.pollInFlight) return;
        if (Date.now() - this.pollStartedAt >= POLL_MAX_MS) {
          this.pollTimedOut = true;
          this.stopPolling();
          warn('ReleaseDetail: generation poll timed out');
          return;
        }
        this.pollInFlight = true;
        this.$store
          .dispatch('RELEASE_NOTES/FETCH_RELEASE_DETAIL', this.id)
          .catch((err) => {
            warn('ReleaseDetail: generation poll failed', err);
          })
          .finally(() => {
            this.pollInFlight = false;
            this.syncPolling();
          });
      },
      /** Banner "Refresh" — also re-arms polling after the cap was hit. */
      manualRefresh() {
        this.pollTimedOut = false;
        this.reload();
      },

      // -------------------------------------------------------------- rename
      startRename() {
        if (this.nameLocked) return;
        this.nameDraft = this.releaseName;
        this.editingName = true;
      },
      cancelRename() {
        this.editingName = false;
        this.nameDraft = '';
      },
      saveName() {
        if (!this.isNameValid || this.savingName) return;
        const name = this.nameDraft.trim();
        this.savingName = true;
        this.$store
          .dispatch('RELEASE_NOTES/RENAME_RELEASE', { id: this.id, data: { name } })
          .then(() => {
            this.editingName = false;
            this.nameDraft = '';
            this.$dialog.notify.success('Release renamed.', {
              position: 'top-right',
              timeout: 3000,
            });
            return this.reload();
          })
          .catch((err) => {
            warn('ReleaseDetail: rename failed', err);
          })
          .finally(() => {
            this.savingName = false;
          });
      },

      // ------------------------------------------------------ header actions
      async confirmArchive() {
        const confirmed = await this.$dialog.confirm({
          title: 'Archive release?',
          text: 'Archiving hides this release from the active list. It cannot be published afterwards.',
          actions: { false: 'Cancel', true: { text: 'Archive', color: 'error' } },
        });
        if (!confirmed) return;
        this.$store
          .dispatch('RELEASE_NOTES/ARCHIVE_RELEASE', this.id)
          .then(() => {
            this.$dialog.notify.success('Release archived.', {
              position: 'top-right',
              timeout: 3000,
            });
            return this.reload();
          })
          .catch((err) => {
            warn('ReleaseDetail: archive failed', err);
          });
      },
      onPublished() {
        this.publishDialog = false;
        this.$dialog.notify.success('Published — emails are being sent.', {
          position: 'top-right',
          timeout: 3000,
        });
        this.reload();
        this.fetchReminders();
      },

      // --------------------------------------------------------- draft history
      openHistory() {
        this.historyDrawer = true;
      },
      fetchDrafts() {
        // History follows whichever stream the Notes tab is showing.
        return this.$store.dispatch('RELEASE_NOTES/FETCH_DRAFTS', { id: this.id, kind: this.notesKind });
      },
      /**
       * The history list is presentational and its rows may not carry content, so the full draft
       * is fetched before the read-only preview opens.
       * @param {object} draft
       */
      previewDraft(draft) {
        if (!draft) return;
        const draftId = draft.id !== undefined ? draft.id : draft.draftId;
        this.previewDraftItem = draft;
        this.previewDialog = true;
        if (draftId === undefined || draftId === null) return;
        this.previewLoading = true;
        this.$store
          .dispatch('RELEASE_NOTES/FETCH_DRAFT', { id: this.id, draftId })
          .then((full) => {
            if (full) this.previewDraftItem = full;
          })
          .catch((err) => {
            warn('ReleaseDetail: draft preview failed', err);
          })
          .finally(() => {
            this.previewLoading = false;
          });
      },
      async confirmRevert(draft) {
        if (!draft) return;
        const draftId = draft.id !== undefined ? draft.id : draft.draftId;
        if (draftId === undefined || draftId === null) return;
        const confirmed = await this.$dialog.confirm({
          title: 'Revert to this version?',
          text: 'The current notes are replaced by this version. The current text stays available in history.',
          actions: { false: 'Cancel', true: { text: 'Revert', color: 'error' } },
        });
        if (!confirmed) return;
        this.$store
          .dispatch('RELEASE_NOTES/REVERT_DRAFT', { id: this.id, draftId })
          .then(() => {
            this.$dialog.notify.success('Reverted to the selected version.', {
              position: 'top-right',
              timeout: 3000,
            });
            this.previewDialog = false;
            this.fetchDrafts();
            return this.reload();
          })
          .catch((err) => {
            warn('ReleaseDetail: revert failed', err);
          });
      },

      // ---------------------------------------------------------- regenerate
      openRegenerate() {
        this.regenerateInstructions = '';
        this.regenerateDialog = true;
      },
      /**
       * Shared by the failed-generation banner and the Items tab: both funnel into the same
       * instructions dialog, then land the user on the Notes tab where the new draft appears.
       */
      submitRegenerate() {
        const instructions = (this.regenerateInstructions || '').trim();
        this.$store
          .dispatch('RELEASE_NOTES/REGENERATE_DRAFT', {
            id: this.id,
            data: { instructions: instructions || null },
          })
          .then(() => {
            this.regenerateDialog = false;
            this.regenerateInstructions = '';
            this.tab = NOTES_TAB;
            this.$dialog.notify.success('Regenerating the notes…', {
              position: 'top-right',
              timeout: 3000,
            });
            return this.reload();
          })
          .catch((err) => {
            warn('ReleaseDetail: regenerate failed', err);
          });
      },

      // ------------------------------------------------------- publish tab
      fetchReminders() {
        return this.$store.dispatch('RELEASE_NOTES/FETCH_REMINDERS', this.id);
      },
      onReminderChanged() {
        this.fetchReminders();
        this.reload();
      },
      sendTest() {
        this.sendingTest = true;
        this.$store
          .dispatch('RELEASE_NOTES/SEND_TEST', this.id)
          .then(() => {
            this.$dialog.notify.success('Test email sent to your address.', {
              position: 'top-right',
              timeout: 3000,
            });
          })
          .catch((err) => {
            warn('ReleaseDetail: send test failed', err);
          })
          .finally(() => {
            this.sendingTest = false;
          });
      },
      async confirmMarkAppReleased() {
        const confirmed = await this.$dialog.confirm({
          title: 'Mark the app as released?',
          text: 'This records the deployment and completes the pending "release the app" reminder.',
          actions: { false: 'Cancel', true: { text: 'Mark released', color: 'primary' } },
        });
        if (!confirmed) return;
        this.$store
          .dispatch('RELEASE_NOTES/MARK_APP_RELEASED', this.id)
          .then(() => {
            this.$dialog.notify.success('Marked as released.', {
              position: 'top-right',
              timeout: 3000,
            });
            this.fetchReminders();
            return this.reload();
          })
          .catch((err) => {
            warn('ReleaseDetail: mark app released failed', err);
          });
      },

      goToList() {
        this.$router.push({ name: 'release-notes' });
      },
    },
  };
</script>

<style scoped lang="scss">
  .release-detail {
    &__name-field {
      max-width: 420px;
    }
  }
</style>
